import * as React from "react";
import { useEffect, useState } from "react";
import { scriptService } from "@modules/scripting/services/ScriptService";
import { modelPool } from "@modules/model";
import "./ScriptTab.component.css";
const lifecycleMap = new Map();
const modelNameCache = new Map();
const scriptNameCache = new Map();
export function ScriptTab() {
    const [rooms, setRooms] = useState([]);
    const [selectedRoom, setSelectedRoom] = useState();
    const [expandedModels, setExpandedModels] = useState(new Set());
    const [expandedScripts, setExpandedScripts] = useState(new Set());
    useEffect(() => {
        const update = () => {
            const ctx = scriptService.context;
            if (!ctx)
                return;
            const roomsArray = [...ctx.rooms.values()].filter(hasScripts);
            setRooms(roomsArray);
            if (selectedRoom && !roomsArray.includes(selectedRoom)) {
                setSelectedRoom(roomsArray.includes(ctx.currentRoom) ? ctx.currentRoom : roomsArray[0]);
                return;
            }
            if (!selectedRoom && ctx.currentRoom && roomsArray.includes(ctx.currentRoom)) {
                setSelectedRoom(ctx.currentRoom);
            }
        };
        update();
        const timer = setInterval(update, 250);
        return () => {
            clearInterval(timer);
        };
    }, [selectedRoom]);
    if (!scriptService.context) {
        return React.createElement("div", { className: "script-tab-container" }, "No scripting context");
    }
    const context = scriptService.context;
    return (React.createElement("div", { className: "script-tab-container" },
        React.createElement("div", { className: "script-rooms" }, rooms.map((room) => {
            var _a, _b;
            const stats = getRoomStats(room);
            const isActive = room === context.currentRoom;
            const isSelected = room === selectedRoom;
            return (React.createElement("div", { key: room.roomUID, className: `script-room-row ${isSelected ? "selected" : ""}`, onClick: () => {
                    setSelectedRoom(room);
                } },
                React.createElement("span", { className: `script-room-name ${isActive ? "active" : ""}` }, (_b = (_a = modelPool.getModel(room.roomUID)) === null || _a === void 0 ? void 0 : _a.getName()) !== null && _b !== void 0 ? _b : room.roomUID),
                React.createElement("span", { className: "script-room-count" },
                    "(",
                    stats.active,
                    "/",
                    stats.total,
                    ")")));
        })),
        React.createElement("div", { className: "script-models" }, selectedRoom &&
            [...selectedRoom.models.values()]
                .sort((a, b) => {
                const aDeleted = isDeletedModel(a);
                const bDeleted = isDeletedModel(b);
                if (aDeleted !== bDeleted)
                    return aDeleted ? 1 : -1;
                return getModelDisplayName(a).localeCompare(getModelDisplayName(b), undefined, { sensitivity: "base" });
            })
                .map((modelData) => {
                const model = modelPool.getModel(modelData.modelUID);
                const isDeleted = model === undefined;
                const modelName = getModelDisplayName(modelData);
                const stats = getModelStats(modelData);
                const colorClass = isDeleted ? "model-deleted" : getModelColorClass(stats.total, stats.good);
                const expanded = expandedModels.has(modelData.modelUID);
                return (React.createElement("div", { key: modelData.modelUID, className: "script-model" },
                    React.createElement("div", { className: `script-model-row ${isDeleted ? "script-model-row-deleted" : ""}`, onClick: () => {
                            toggleExpanded(modelData.modelUID);
                        } },
                        React.createElement("span", { className: `script-model-name ${isDeleted ? "script-model-name-deleted" : ""}` }, modelName),
                        React.createElement("span", { className: `script-model-count ${colorClass}` },
                            "(",
                            stats.good,
                            "/",
                            stats.total,
                            ")")),
                    expanded && (React.createElement("div", { className: "script-list" },
                        React.createElement("div", { className: "script-section" },
                            React.createElement("div", { className: "script-section-label" }, "General"),
                            React.createElement("table", { className: "script-info-table" },
                                React.createElement("tbody", null,
                                    React.createElement("tr", null,
                                        React.createElement("th", null, "UID"),
                                        React.createElement("td", null, modelData.modelUID))))),
                        React.createElement("div", { className: "script-section" },
                            React.createElement("div", { className: "script-section-label" }, "Scripts"),
                            [...modelData.scripts.values()].map((script) => {
                                const lifecycle = getScriptLifecycle(script);
                                const seconds = ((Date.now() - lifecycle.lastChange) / 1000).toFixed(1);
                                const manual = isManualScript(script);
                                const scriptName = getScriptDisplayName(script);
                                const scriptMethods = getScriptMethodNames(script);
                                const scriptMetadata = scriptService.getScriptMetadata(script);
                                const expandedScript = expandedScripts.has(script.scriptIdx);
                                return (React.createElement("div", { key: script.scriptIdx, className: "script-entry" },
                                    React.createElement("div", { className: "script-row", onClick: () => {
                                            toggleExpandedScript(script.scriptIdx);
                                        } },
                                        React.createElement("span", { className: `script-name ${manual ? "manual-script" : ""}` },
                                            scriptName,
                                            manual && (React.createElement("span", { className: "script-manual-badge", title: "autoExecute disabled" }, "MANUAL"))),
                                        React.createElement("span", { className: `script-state ${lifecycle.state.toLowerCase()}` },
                                            lifecycle.state,
                                            " (",
                                            seconds,
                                            "s)")),
                                    expandedScript && (React.createElement("div", { className: "script-details" },
                                        React.createElement("div", { className: "script-section" },
                                            React.createElement("div", { className: "script-section-label" }, "General"),
                                            React.createElement("table", { className: "script-info-table" },
                                                React.createElement("tbody", null,
                                                    React.createElement("tr", null,
                                                        React.createElement("th", null, "Script ID"),
                                                        React.createElement("td", null, script.scriptId))))),
                                        React.createElement("div", { className: "script-section" },
                                            React.createElement("div", { className: "script-section-label" }, "Methods"),
                                            React.createElement("table", { className: "script-info-table" },
                                                React.createElement("tbody", null,
                                                    scriptMethods.map((methodName) => {
                                                        var _a;
                                                        return (React.createElement("tr", { key: methodName },
                                                            React.createElement("th", null, methodName),
                                                            React.createElement("td", null, getLastCalledLabel((_a = scriptMetadata === null || scriptMetadata === void 0 ? void 0 : scriptMetadata.methods.get(methodName)) === null || _a === void 0 ? void 0 : _a.lastCalledAt))));
                                                    }),
                                                    scriptMethods.length === 0 && (React.createElement("tr", null,
                                                        React.createElement("th", null, "No methods"),
                                                        React.createElement("td", null))))))))));
                            }))))));
            }))));
    function toggleExpanded(modelUID) {
        setExpandedModels((prev) => {
            const next = new Set(prev);
            if (next.has(modelUID))
                next.delete(modelUID);
            else
                next.add(modelUID);
            return next;
        });
    }
    function toggleExpandedScript(scriptIdx) {
        setExpandedScripts((prev) => {
            const next = new Set(prev);
            if (next.has(scriptIdx))
                next.delete(scriptIdx);
            else
                next.add(scriptIdx);
            return next;
        });
    }
}
function hasScripts(room) {
    for (const model of room.models.values()) {
        if (model.scripts.size > 0) {
            return true;
        }
    }
    return false;
}
function getScriptLifecycle(script) {
    let newState;
    if (script.isDestroyed)
        newState = "DESTROYED";
    else if (!script.scriptInstance)
        newState = "LOADING";
    else if (!script.isInitialized)
        newState = "CREATED";
    else if (script.isInitialized && !script.isRegisteredInRoom)
        newState = "INITIALIZED";
    else if (script.isRegisteredInRoom && !script.room.isActive)
        newState = "STARTED";
    else if (script.room.isActive)
        newState = "ACTIVE";
    else
        newState = "INACTIVE";
    const existing = lifecycleMap.get(script.scriptIdx);
    if (!existing) {
        const info = { state: newState, lastChange: script.createdAt };
        lifecycleMap.set(script.scriptIdx, info);
        return info;
    }
    if (existing.state !== newState) {
        existing.state = newState;
        existing.lastChange = Date.now();
    }
    return existing;
}
function getModelDisplayName(modelData) {
    var _a, _b;
    const modelName = (_a = modelPool.getModel(modelData.modelUID)) === null || _a === void 0 ? void 0 : _a.getName();
    if (modelName !== undefined) {
        modelNameCache.set(modelData.modelUID, modelName);
        return modelName;
    }
    return (_b = modelNameCache.get(modelData.modelUID)) !== null && _b !== void 0 ? _b : String(modelData.modelUID);
}
function getScriptDisplayName(script) {
    var _a;
    const scriptName = script.scriptName;
    if (scriptName !== undefined) {
        scriptNameCache.set(script.scriptIdx, scriptName);
        return scriptName;
    }
    return (_a = scriptNameCache.get(script.scriptIdx)) !== null && _a !== void 0 ? _a : script.scriptId;
}
function isDeletedModel(modelData) {
    return modelPool.getModel(modelData.modelUID) === undefined;
}
function getRoomStats(room) {
    let total = 0;
    let active = 0;
    for (const model of room.models.values()) {
        for (const script of model.scripts.values()) {
            if (!isNonDestroyedScript(script))
                continue;
            total++;
            if (script.isInitialized)
                active++;
        }
    }
    return { total, active };
}
function getModelStats(model) {
    let total = 0;
    let good = 0;
    for (const script of model.scripts.values()) {
        total++;
        if (script.isDestroyed === true || script.isInitialized) {
            good++;
        }
    }
    return { total, good };
}
function getModelColorClass(total, good) {
    if (total === 0)
        return "model-red";
    if (good === total)
        return "model-green";
    if (good > 0)
        return "model-orange";
    return "model-red";
}
function isManualScript(script) {
    return script.autoExecute === false;
}
function isNonDestroyedScript(script) {
    return script.isDestroyed !== true;
}
function getScriptMethodNames(script) {
    const scriptInstance = script.scriptInstance;
    if (scriptInstance === undefined)
        return [];
    const prototype = Reflect.getPrototypeOf(scriptInstance);
    if (prototype === undefined || prototype === null)
        return [];
    return Object
        .getOwnPropertyNames(prototype)
        .filter((methodName) => {
        if (methodName === "constructor")
            return false;
        const descriptor = Object.getOwnPropertyDescriptor(prototype, methodName);
        return typeof (descriptor === null || descriptor === void 0 ? void 0 : descriptor.value) === "function";
    })
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}
function getLastCalledLabel(lastCalledAt) {
    if (lastCalledAt === undefined)
        return "";
    return new Date(lastCalledAt).toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false
    });
}
